import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import type { SendBulkReminderRequest } from '@/types/scheduling-api';

/**
 * POST /api/Appointment/SendBulkReminder
 *
 * Mirrors Flutter `SchedulingDataSourceImpl.sendBulkReminder`
 * (`scheduling_data_source_impl.dart:584-603`). Drives the FU Requests
 * tab Send Reminder kebab + bulk-select bar (PROD-851 PR-3).
 *
 * Wire shape verified against the 2026-05-21 dev curl:
 *   POST /api/Appointment/SendBulkReminder
 *   { "fuRequestIds": ["<guid>", ...] }
 *
 * Even single-row "Send Reminder" via the kebab ships a 1-element
 * array (Flutter `fu_requests_tab.dart:498-503`).
 *
 * **No special headers required.** All headers on the dev curl
 * (`ipaddress`, `usertimezone`, `roleid`, `userid`, `organizationid`,
 * `practiceid`, `apptype`) are already injected by `apiClient`. This
 * endpoint does NOT exhibit the PROD-762 gRPC-metadata-null crash
 * pattern (verified: 2xx empty body, no fan-out).
 *
 * Backend behaviour on 200:
 *   - Each row's `lastReminder` flips to "now"
 *   - Notifications fire via the patient's preferred channel
 *
 * Cache invalidation: on 200 invalidate the `['follow-up-requests',
 * ...]` query family so the FU tab refetches and "Last Reminder"
 * column updates. Same prefix as `useCancelFollowUpRequest` — kept
 * deliberately so a future `fuRequestCount` toolbar badge can sit
 * under the same family and pick up the same refresh.
 */

interface SendBulkReminderVariables {
  request: SendBulkReminderRequest;
}

export function useSendBulkReminder(): UseMutationResult<
  void,
  Error,
  SendBulkReminderVariables
> {
  const qc = useQueryClient();
  return useMutation<void, Error, SendBulkReminderVariables>({
    mutationFn: ({ request }) =>
      post<void>('/api/Appointment/SendBulkReminder', request),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['follow-up-requests'] });
    },
  });
}
